import {
  handleSetUrlSearchParams,
  setTimeStartEndMonth,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export const setTimeStartEnd = (
  date: Date | string | null,
  type: 'start' | 'end'
) => {
  if (!date) return '';
  if (type === 'start') {
    return DateTime.fromJSDate(new Date(date)).startOf('day').toUTC().toISO();
  }
  return DateTime.fromJSDate(new Date(date)).endOf('day').toUTC().toISO();
};

export const dateIsToday = (dateToCheck: string) => {
  const date = DateTime.fromISO(dateToCheck).toISODate();
  const today = DateTime.local().toISODate();
  return date === today;
};

// Generic by `createdAt`. Works for both the legacy NewTransactionResponse and
// the new NewTransactionItem shapes.
export const groupTransactionsByDate = <T extends { createdAt: string }>(
  transactions: T[]
): { date: string; items: T[] }[] => {
  const groupedMap = new Map<string, T[]>();
  transactions.forEach((item) => {
    const date = DateTime.fromISO(item.createdAt).toISODate() || '';
    if (!groupedMap.has(date)) groupedMap.set(date, []);
    groupedMap.get(date)!.push(item);
  });
  return Array.from(groupedMap, ([date, items]) => ({ date, items }));
};

// Shared URL-driven date-range + page state for the Transactions screens.
// Returns the resolved `from`/`to`/`page` plus the date-picker controls and
// the precomputed display label. Callers can still use `searchParams` /
// `setSearchParams` to read/write any additional page-specific URL params.
export const useTransactionsDateRange = () => {
  const today = new Date();
  const INITIAL_DATE = [
    setTimeStartEndMonth(today, 'start'),
    setTimeStartEndMonth(today, 'today'),
  ];
  const [searchParams, setSearchParams] = useSearchParams();

  const from = searchParams.get('from') ?? (INITIAL_DATE[0] as string);
  const to = searchParams.get('to') ?? (INITIAL_DATE[1] as string);
  const [dateInput, setDateInput] = useState<Date[] | string[]>([
    new Date(from),
    new Date(to),
  ]);
  const page = Number(searchParams.get('page') ?? '1');
  const hasDateFilter = searchParams.has('from') || searchParams.has('to');

  const setDate = (date: Date[] | string[]) => {
    handleSetUrlSearchParams('from', date[0] as string, setSearchParams);
    handleSetUrlSearchParams('to', date[1] as string, setSearchParams);
  };

  const setPage = (next: number) =>
    handleSetUrlSearchParams('page', next, setSearchParams, true);

  const handleDateChange = (date: Date[] | string[]) => setDateInput(date);

  const clearDate = () => {
    handleSetUrlSearchParams('from', null, setSearchParams);
    handleSetUrlSearchParams('to', null, setSearchParams);
    handleSetUrlSearchParams('page', null, setSearchParams, true);
    setDateInput([
      new Date(INITIAL_DATE[0] as string),
      new Date(INITIAL_DATE[1] as string),
    ]);
  };

  const handleDateApply = () => {
    if (Array.isArray(dateInput) && dateInput.length < 2) {
      setDate([
        setTimeStartEnd(dateInput[0] as Date | string, 'start')!,
        setTimeStartEnd(dateInput[0] as Date | string, 'end')!,
      ]);
      setDateInput([dateInput[0] as Date, dateInput[0] as Date]);
    } else {
      setDate([
        setTimeStartEnd(dateInput[0] as Date | string, 'start')!,
        setTimeStartEnd(dateInput[1] as Date | string, 'end')!,
      ]);
    }
  };

  const dateFromFormatted = DateTime.fromISO(from).toFormat('MMM dd, yyyy');
  const dateToFormatted = DateTime.fromISO(to).toFormat('MMM dd, yyyy');
  const dateLabel =
    dateIsToday(from) && dateIsToday(to)
      ? 'Today'
      : `${dateFromFormatted} - ${dateToFormatted}`;

  return {
    from,
    to,
    page,
    setPage,
    dateInput,
    handleDateChange,
    handleDateApply,
    clearDate,
    hasDateFilter,
    dateLabel,
    searchParams,
    setSearchParams,
  };
};

// Order/delivery links inside transaction modals open in a new tab so the
// transaction list (and its filters/scroll position) stays where it was.
export const openInNewTab = (path: string) =>
  window.open(path, '_blank', 'noopener,noreferrer');
